import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trophy, XCircle, GripVertical, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const money = (n) => "$" + (n || 0).toLocaleString();

export default function Pipelines() {
  const { activeSubId } = useAuth();
  const [pipelines, setPipelines] = useState([]);
  const [current, setCurrent] = useState(null);
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [dealOpen, setDealOpen] = useState(false);
  const [pipeOpen, setPipeOpen] = useState(false);
  const [form, setForm] = useState({ title: "", value: "", contact_id: "", stage_id: "" });
  const [pipeForm, setPipeForm] = useState({ name: "", stages: "New Lead, Contacted, Qualified, Proposal, Won, Lost" });

  const loadPipelines = useCallback(async () => {
    if (!activeSubId) return;
    const { data } = await api.get(`/pipelines?sub_account_id=${activeSubId}`);
    setPipelines(data);
    setCurrent((prev) => data.find((p) => p.id === prev?.id) || data[0] || null);
    const { data: cs } = await api.get(`/contacts?sub_account_id=${activeSubId}`);
    setContacts(cs);
    setLoading(false);
  }, [activeSubId]);

  const loadDeals = useCallback(async (pid) => {
    if (!pid) { setDeals([]); return; }
    const { data } = await api.get(`/pipelines/${pid}/deals`);
    setDeals(data);
  }, []);

  useEffect(() => { loadPipelines(); }, [loadPipelines]);
  useEffect(() => { if (current) loadDeals(current.id); }, [current, loadDeals]);

  const onDrop = async (stageId) => {
    setOverStage(null);
    if (!dragId) return;
    const deal = deals.find((d) => d.id === dragId);
    if (!deal || deal.stage_id === stageId) { setDragId(null); return; }
    setDeals((ds) => ds.map((d) => (d.id === dragId ? { ...d, stage_id: stageId } : d)));
    setDragId(null);
    const stage = current.stages.find((s) => s.id === stageId);
    const status = /won/i.test(stage?.name) ? "won" : /lost/i.test(stage?.name) ? "lost" : "open";
    await api.put(`/deals/${deal.id}`, { stage_id: stageId, status });
    loadDeals(current.id);
  };

  const createDeal = async () => {
    try {
      await api.post(`/pipelines/${current.id}/deals`, {
        title: form.title, value: parseFloat(form.value) || 0,
        contact_id: form.contact_id || null, stage_id: form.stage_id || current.stages[0].id,
      });
      toast.success("Deal added"); setDealOpen(false);
      setForm({ title: "", value: "", contact_id: "", stage_id: "" });
      loadDeals(current.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const createPipeline = async () => {
    try {
      await api.post(`/pipelines?sub_account_id=${activeSubId}`, {
        name: pipeForm.name, stages: pipeForm.stages.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Pipeline created"); setPipeOpen(false); setPipeForm({ name: "", stages: pipeForm.stages });
      loadPipelines();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delDeal = async (id) => { await api.delete(`/deals/${id}`); loadDeals(current.id); };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Sales CRM</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Pipelines</h1>
        </div>
        <div className="flex items-center gap-3">
          {pipelines.length > 0 && (
            <Select value={current?.id} onValueChange={(v) => setCurrent(pipelines.find((p) => p.id === v))}>
              <SelectTrigger className="w-[200px]" data-testid="pipeline-select"><SelectValue /></SelectTrigger>
              <SelectContent>{pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Dialog open={pipeOpen} onOpenChange={setPipeOpen}>
            <DialogTrigger asChild><Button variant="outline" className="gap-2" data-testid="new-pipeline-button"><Plus className="w-4 h-4" />Pipeline</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New pipeline</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5"><Label>Name</Label><Input value={pipeForm.name} onChange={(e) => setPipeForm({ ...pipeForm, name: e.target.value })} data-testid="pipeline-name-input" /></div>
                <div className="space-y-1.5"><Label>Stages (comma separated)</Label><Input value={pipeForm.stages} onChange={(e) => setPipeForm({ ...pipeForm, stages: e.target.value })} data-testid="pipeline-stages-input" /></div>
              </div>
              <DialogFooter><Button onClick={createPipeline} disabled={!pipeForm.name} data-testid="save-pipeline-button">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          {current && (
            <Dialog open={dealOpen} onOpenChange={setDealOpen}>
              <DialogTrigger asChild><Button className="gap-2 font-semibold" data-testid="add-deal-button"><Plus className="w-4 h-4" />Deal</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New deal</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="deal-title-input" /></div>
                  <div className="space-y-1.5"><Label>Value ($)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="deal-value-input" /></div>
                  <div className="space-y-1.5"><Label>Contact</Label>
                    <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
                      <SelectTrigger data-testid="deal-contact-select"><SelectValue placeholder="Link a contact" /></SelectTrigger>
                      <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label>Stage</Label>
                    <Select value={form.stage_id} onValueChange={(v) => setForm({ ...form, stage_id: v })}>
                      <SelectTrigger data-testid="deal-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                      <SelectContent>{current.stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <DialogFooter><Button onClick={createDeal} disabled={!form.title} data-testid="save-deal-button">Add deal</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {!current ? (
        <Card className="p-16 text-center text-muted-foreground bg-card border-border/70">No pipeline yet. Create one to get started.</Card>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {current.stages.map((stage) => {
            const stageDeals = deals.filter((d) => d.stage_id === stage.id);
            const total = stageDeals.reduce((s, d) => s + (d.value || 0), 0);
            const isWon = /won/i.test(stage.name), isLost = /lost/i.test(stage.name);
            return (
              <div key={stage.id} className="w-[290px] shrink-0" data-testid={`stage-column-${stage.id}`}
                onDragOver={(e) => { e.preventDefault(); setOverStage(stage.id); }}
                onDragLeave={() => setOverStage((s) => (s === stage.id ? null : s))}
                onDrop={() => onDrop(stage.id)}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    {isWon && <Trophy className="w-4 h-4 text-emerald-400" />}
                    {isLost && <XCircle className="w-4 h-4 text-rose-400" />}
                    <span className="font-display font-semibold text-sm">{stage.name}</span>
                    <span className="text-xs text-muted-foreground bg-secondary rounded-full px-2 py-0.5">{stageDeals.length}</span>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">{money(total)}</span>
                </div>
                <div className={`space-y-2.5 min-h-[120px] rounded-xl p-2 transition-colors ${overStage === stage.id ? "bg-primary/10 ring-1 ring-primary/40" : "bg-secondary/20"}`}>
                  {stageDeals.map((d) => (
                    <Card key={d.id} draggable
                      onDragStart={() => setDragId(d.id)} onDragEnd={() => setDragId(null)}
                      data-testid={`deal-card-${d.id}`}
                      className={`p-3.5 bg-card border-border/70 cursor-grab active:cursor-grabbing group hover:border-primary/50 transition-colors ${dragId === d.id ? "opacity-40" : ""}`}>
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-4 h-4 text-muted-foreground/40 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{d.title}</p>
                          {d.contact_name && <p className="text-xs text-muted-foreground truncate mt-0.5">{d.contact_name}</p>}
                          <p className="text-sm font-mono font-semibold text-emerald-400 mt-1.5">{money(d.value)}</p>
                        </div>
                        <button onClick={() => delDeal(d.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity" data-testid={`delete-deal-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
